import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "../../../../utils/apparifySwal";
import { FileText, Rocket } from "lucide-react";
import { businessSwalBase } from "../../../utils/businessSwal";
import { businessProductsAPI } from "../../../services/businessProductsService";
import { getImageUrl } from "../../../../services/api";

/* ── helpers ─────────────────────────────────────────────── */

function peso(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `₱${num.toFixed(2)}`;
}

function priceRange(variants) {
  const prices = (variants || [])
    .map((v) => Number(v?.price ?? 0))
    .filter((x) => Number.isFinite(x) && x >= 0);

  if (!prices.length) return "—";

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? peso(min) : `${peso(min)} – ${peso(max)}`;
}

function optionList(options) {
  return (options || [])
    .filter((o) => (o?.name || "").trim() && (o?.values || []).length)
    .map((o) => ({ name: o.name.trim(), values: o.values }));
}

/* ── validation ──────────────────────────────────────────── */

function validate(state) {
  const errors = [];

  if (!state?.basic?.name?.trim()) errors.push("Product name is required.");
  if (!state?.basic?.category?.trim()) errors.push("Category is required.");

  const imgs = state?.media?.productImages || [];
  if (imgs.length === 0) errors.push("At least 1 product image is required.");

  const variants = state?.variants || [];
  if (variants.length === 0) errors.push("Variants are required before publishing.");

  const skuSet = new Set();

  variants.forEach((v, idx) => {
    const label = `Variant #${idx + 1}`;
    const sku = String(v?.sku || "").trim();

    if (!sku) {
      errors.push(`${label}: SKU is required.`);
    } else {
      const upper = sku.toUpperCase();
      if (skuSet.has(upper)) errors.push(`Duplicate SKU found: ${upper}`);
      else skuSet.add(upper);
    }

    const price = Number(v?.price ?? 0);
    if (!Number.isFinite(price) || price <= 0) {
      errors.push(`${label}: Price must be greater than 0.`);
    } else {
      const parts = String(v?.price).split('.');
      if (parts[1] && parts[1].length > 2) {
        errors.push(`${label}: Price must have at most 2 decimal places.`);
      }
    }

    const ov = v?.optionValues || {};
    if (!ov || Object.keys(ov).length === 0) {
      errors.push(`${label}: Option values missing (e.g. Size/Color).`);
    }
  });

  return errors;
}

function checklist(state) {
  const nameOk = !!state?.basic?.name?.trim();
  const catOk = !!state?.basic?.category?.trim();
  const imgOk = (state?.media?.productImages || []).length > 0;

  const variants = state?.variants || [];
  const variantsOk = variants.length > 0;

  const skuSet = new Set();
  let skuOk = true;

  for (const v of variants) {
    const sku = String(v?.sku || "").trim().toUpperCase();
    if (!sku || skuSet.has(sku)) {
      skuOk = false;
      break;
    }
    skuSet.add(sku);
  }

  const pricesOk = variants.every((v) => {
    const p = Number(v?.price ?? 0);
    if (!Number.isFinite(p) || p <= 0) return false;
    const parts = String(v?.price).split('.');
    return !(parts[1] && parts[1].length > 2);
  });

  return [
    { label: "Basic info complete (name + category)", ok: nameOk && catOk },
    { label: "At least 1 product image (cover)", ok: imgOk },
    { label: "Variants exist", ok: variantsOk },
    { label: "SKUs are unique and present", ok: variantsOk && skuOk },
    { label: "Prices are valid (≥ 0)", ok: variantsOk && pricesOk },
  ];
}

function extractErrorMessage(err) {
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong. Check backend validation and endpoints.";
  return String(msg);
}

/* ── component ───────────────────────────────────────────── */

export default function StepPublish({ state, payload, ensureDraft }) {
  const navigate = useNavigate();

  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const options = useMemo(() => optionList(state?.options), [state?.options]);
  const errors = useMemo(() => validate(state), [state]);

  const activeVariant = useMemo(() => {
    const vars = state?.variants || [];
    if (!vars.length) return null;

    const match = vars.find((v) =>
      Object.entries(selected).every(([k, val]) => v?.optionValues?.[k] === val)
    );

    return match || vars[0];
  }, [selected, state?.variants]);

  const img =
    getImageUrl(
      activeVariant?.imageUrl ||
      state?.media?.productImages?.[0]
    ) || "https://via.placeholder.com/600x600?text=Preview";

  const range = useMemo(() => priceRange(state?.variants), [state?.variants]);

  const checks = useMemo(() => checklist(state), [state]);
  const ready = checks.every((c) => c.ok) && errors.length === 0;

  /* ── Save handler (shared by both actions) ─────────────── */

  const handleSave = useCallback(
    async (action) => {
      if (busy) return;

      const isDraft = action === "draft";
      const targetStatus = isDraft ? "DRAFT" : "LISTED";

      // Listing requires readiness
      if (!isDraft && !ready) {
        await Swal.fire({
          ...businessSwalBase,
          icon: "error",
          title: "Not ready to list",
          html: "Please fix the checklist items before listing.",
          confirmButtonText: "OK",
        });
        return;
      }

      // Lazily create draft if it doesn't exist yet
      let draftId = state?.draftId;
      if (!draftId && ensureDraft) {
        draftId = await ensureDraft();
      }
      if (!draftId) {
        await Swal.fire({
          ...businessSwalBase,
          icon: "error",
          title: "Missing draft",
          html: "Draft ID is missing. Check draft creation endpoint / API base URL.",
          confirmButtonText: "OK",
        });
        return;
      }

      // Confirmation for both actions
      if (isDraft) {
        const confirm = await Swal.fire({
          ...businessSwalBase,
          icon: "question",
          title: "Save as draft?",
          html: "The product will be saved but <b>not visible</b> to customers. You can continue editing later.",
          showCancelButton: true,
          confirmButtonText: "Yes, Save Draft",
          cancelButtonText: "Cancel",
        });
        if (!confirm.isConfirmed) return;
      } else {
        const confirm = await Swal.fire({
          ...businessSwalBase,
          icon: "question",
          title: "List this product for sale?",
          html:
            "This product will be <b>visible on your storefront</b> immediately.<br/>" +
            "Customers will be able to browse and purchase it.",
          showCancelButton: true,
          confirmButtonText: "Yes, List for Sale",
          cancelButtonText: "Not yet",
        });
        if (!confirm.isConfirmed) return;
      }

      setBusy(true);

      Swal.fire({
        ...businessSwalBase,
        title: isDraft ? "Saving draft…" : "Listing product…",
        html: "Sending product data to server",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        await businessProductsAPI.publishDraft(draftId, {
          ...payload,
          status: targetStatus,
        });

        Swal.close();

        await Swal.fire({
          ...businessSwalBase,
          icon: "success",
          title: isDraft ? "Draft saved!" : "Product listed!",
          html: isDraft
            ? "You can continue editing from your Products page."
            : "Customers can now see this product in your store.",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/business/products");
      } catch (e) {
        Swal.close();

        await Swal.fire({
          ...businessSwalBase,
          icon: "error",
          title: "Save failed",
          html: extractErrorMessage(e),
          confirmButtonText: "OK",
        });
      } finally {
        setBusy(false);
      }
    },
      [busy, ready, state?.draftId, payload, navigate, ensureDraft]
  );

  /* ── render ─────────────────────────────────────────────── */

  return (
    <div className="space-y-4">
      {/* ── Checklist banner ─────────────────────────────── */}
      <div
        className={
          "rounded-2xl border p-4 " +
          (ready
            ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/40"
            : "border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/40")
        }
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Review & Save
            </div>
            <div className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
              Review your product details before saving.
            </div>
          </div>

          <span
            className={
              "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold " +
              (ready
                ? "border-emerald-200 bg-white text-emerald-800 dark:border-emerald-900/40 dark:bg-transparent dark:text-emerald-200"
                : "border-amber-200 bg-white text-amber-800 dark:border-amber-900/40 dark:bg-transparent dark:text-amber-200")
            }
          >
            {ready ? "Ready to list" : "Not ready to list"}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {checks.map((c) => (
            <div
              key={c.label}
              className={
                "rounded-xl border px-3 py-2 text-xs " +
                (c.ok
                  ? "border-emerald-200 bg-white text-emerald-800 dark:border-emerald-900/40 dark:bg-transparent dark:text-emerald-200"
                  : "border-amber-200 bg-white text-amber-800 dark:border-amber-900/40 dark:bg-transparent dark:text-amber-200")
              }
            >
              {c.ok ? "✓" : "!"} {c.label}
            </div>
          ))}
        </div>
      </div>

      {/* ── Preview Card ─────────────────────────────────── */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="h-40 w-full overflow-hidden rounded-xl border border-zinc-200 sm:h-28 sm:w-28 dark:border-zinc-800">
            <img src={img} alt="" className="h-full w-full object-cover" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              {state?.basic?.name?.trim() || "Product Name"}
            </div>

            <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              {range} <span className="mx-2">•</span> {state?.basic?.category || "Category"}
            </div>

            <div className="mt-3 space-y-2">
              {options.map((o) => (
                <div key={o.name} className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                  <div className="w-full text-xs text-zinc-600 sm:w-16 dark:text-zinc-400">{o.name}</div>
                  <select
                    className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs outline-none
                               focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200
                               dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100
                               dark:focus:border-zinc-600 dark:focus:ring-zinc-800"
                    value={selected[o.name] || ""}
                    onChange={(e) => setSelected((p) => ({ ...p, [o.name]: e.target.value }))}
                  >
                    <option value="">Select</option>
                    {o.values.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-500">
              Variant SKU: <span className="font-medium">{activeVariant?.sku || "—"}</span>
              <span className="mx-2">•</span>
              Price:{" "}
              <span className="font-medium">
                {activeVariant ? peso(activeVariant?.price) : "—"}
              </span>
            </div>
          </div>
        </div>

        {state?.basic?.description?.trim() ? (
          <div className="mt-4 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
            {state.basic.description}
          </div>
        ) : null}
      </div>

      {/* ── Errors list ──────────────────────────────────── */}
      {errors.length > 0 && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-xs text-amber-800
                     dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <div className="font-semibold">Needs attention</div>
          <div className="mt-2 space-y-1">
            {errors.slice(0, 6).map((e) => (
              <div key={e}>• {e}</div>
            ))}
            {errors.length > 6 && <div>…and {errors.length - 6} more</div>}
          </div>
        </div>
      )}

      {/* ── Action cards ─────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Save as Draft */}
        <div className="flex flex-col rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-[#121214]">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800">
              <FileText className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
            </div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Save as Draft
            </div>
          </div>

          <p className="mt-3 flex-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
            Keep this product <b>hidden</b>. You can continue editing and
            list it later from your Products page.
          </p>

          <button
            onClick={() => handleSave("draft")}
            disabled={busy}
            className="mt-4 w-full rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-medium
                       text-zinc-900 transition hover:bg-zinc-50 disabled:opacity-40
                       dark:border-zinc-700 dark:bg-transparent dark:text-zinc-100 dark:hover:bg-zinc-800"
          >
            {busy ? "Saving…" : "Save Draft"}
          </button>
        </div>

        {/* Save & List for Sale */}
        <div
          className={
            "flex flex-col rounded-2xl border p-5 transition " +
            (ready
              ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/30"
              : "border-zinc-200 bg-zinc-50 opacity-60 dark:border-zinc-800 dark:bg-zinc-900/30")
          }
        >
          <div className="flex items-center gap-2.5">
            <div
              className={
                "flex h-9 w-9 items-center justify-center rounded-xl " +
                (ready
                  ? "bg-emerald-100 dark:bg-emerald-900/50"
                  : "bg-zinc-100 dark:bg-zinc-800")
              }
            >
              <Rocket
                className={
                  "h-4 w-4 " +
                  (ready
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-zinc-400 dark:text-zinc-500")
                }
              />
            </div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Save & List for Sale
            </div>
          </div>

          <p className="mt-3 flex-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
            Make this product <b>visible on your storefront</b> immediately.
            Customers will be able to browse and purchase it.
          </p>

          {!ready && (
            <p className="mt-2 text-[11px] font-medium text-amber-600 dark:text-amber-400">
              Complete the checklist above to enable this option.
            </p>
          )}

          <button
            onClick={() => handleSave("live")}
            disabled={!ready || busy}
            className="mt-4 w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium
                       text-white transition hover:bg-emerald-700 disabled:opacity-40
                       dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400"
          >
            {busy ? "Listing…" : "List for Sale"}
          </button>
        </div>
      </div>

    </div>
  );
}
